// Espelha GovHub.Domain.Core.Adm (FuncionalidadeAdm, SituacaoCliente, TipoCliente, FuncaoFuncionario, TipoCalculoVeiculo,
// AcaoAdm, TipoRegistroAdm) e o TipoBanner do portal

type ChipColor = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

// Código do módulo Administração (SIGAF.tblModulo); não é contratável por cliente
export const ADM_MODULE_ID = 11;

// Códigos das funcionalidades da administração: cada tela exige o seu; o gestor da empresa tem todos
export const AdmFunctionality = {
    Company: 'adm.empresa',
    News: 'adm.noticias',
    Links: 'adm.links',
    Banners: 'adm.banners',
    Clients: 'adm.clientes',
    ClientModules: 'adm.clientes.modulos',
    ClientVehicles: 'adm.clientes.veiculos',
    DocumentTypes: 'adm.tipos-documento',
    PublicationAgencies: 'adm.orgaos-publicacao',
    PublicationVehicles: 'adm.veiculos-publicacao',
    AccessProfiles: 'adm.perfis-acesso',
    AccessGroups: 'adm.grupos-acesso',
    Users: 'adm.usuarios',
    ClientUsers: 'adm.usuarios.clientes',
    Audit: 'adm.auditoria',
    GedProcessing: 'adm.processamento-ged',
    BackgroundServices: 'adm.servicos-segundo-plano',
} as const;

export type AdmFunctionalityCode = typeof AdmFunctionality[keyof typeof AdmFunctionality];

// Abas do detalhe do cliente: quem tem só uma delas também chega ao cliente
export const CLIENT_AREA_FUNCTIONALITIES: AdmFunctionalityCode[] = [
    AdmFunctionality.Clients, AdmFunctionality.ClientModules, AdmFunctionality.ClientVehicles, AdmFunctionality.ClientUsers,
];

// Situação do cliente (IndStatusCliente), independente do atendimento (IndAtivo)
export enum ClientStatus {
    Active = 1,
    Inactive = 2,
    Blocked = 3,
}

export const CLIENT_STATUS_LABEL: Record<ClientStatus, string> = {
    [ClientStatus.Active]: 'Ativo',
    [ClientStatus.Inactive]: 'Inativo',
    [ClientStatus.Blocked]: 'Bloqueado',
};

export const CLIENT_STATUS_COLOR: Record<ClientStatus, ChipColor> = {
    [ClientStatus.Active]: 'success',
    [ClientStatus.Inactive]: 'default',
    [ClientStatus.Blocked]: 'error',
};

export enum ClientType {
    CityHall = 1,
    CityCouncil = 2,
    Autarchy = 3,
    Fund = 4,
    PrivateCompany = 5,
    Other = 6,
}

export const CLIENT_TYPE_LABEL: Record<ClientType, string> = {
    [ClientType.CityHall]: 'Prefeitura',
    [ClientType.CityCouncil]: 'Câmara',
    [ClientType.Autarchy]: 'Autarquia',
    [ClientType.Fund]: 'Fundo',
    [ClientType.PrivateCompany]: 'Empresa privada',
    [ClientType.Other]: 'Outros',
};

// Situação do módulo para o cliente, calculada pela API com a data de hoje (Brasília)
export type ClientModuleSituation = 'active' | 'scheduled' | 'ended' | 'inactive' | 'notContracted';

export const CLIENT_MODULE_SITUATION_LABEL: Record<ClientModuleSituation, string> = {
    active: 'Ativo',
    scheduled: 'Agendado',
    ended: 'Encerrado',
    inactive: 'Inativo',
    notContracted: 'Não contratado',
};

export const CLIENT_MODULE_SITUATION_COLOR: Record<ClientModuleSituation, ChipColor> = {
    active: 'success',
    scheduled: 'info',
    ended: 'warning',
    inactive: 'default',
    notContracted: 'default',
};

// Ligado no cadastro (mesmo fora do período): pode ser desativado
export const isModuleSwitchedOn = (situation?: string) => situation === 'active' || situation === 'scheduled' || situation === 'ended';

// Módulos que se contratam por cliente (os ids de tblModulo); a lista do cliente vem da API
export const CONTRACTABLE_MODULE_IDS = [1, 2, 3, 4, 7, 8, 9, 10];

// Diário Oficial e Portal Municipal: ativar o portal ativa o diário; o diário não sai com o portal ativo
export const OFFICIAL_DIARY_MODULE_ID = 1;
export const MUNICIPAL_PORTAL_MODULE_ID = 3;

// Função do funcionário (tblPessoaFuncionario): o gestor tem acesso total à administração
export enum EmployeeFunction {
    Manager = 1,
    Attendant = 2,
    Financial = 3,
    Commercial = 4,
    Layout = 5,
}

export const EMPLOYEE_FUNCTION_LABEL: Record<EmployeeFunction, string> = {
    [EmployeeFunction.Manager]: 'Gestor',
    [EmployeeFunction.Attendant]: 'Atendente',
    [EmployeeFunction.Financial]: 'Financeiro',
    [EmployeeFunction.Commercial]: 'Comercial',
    [EmployeeFunction.Layout]: 'Diagramador',
};

// Filtro "tipo" da listagem de usuários
export enum UserKind {
    Company = 1,
    Client = 2,
}

export const USER_KIND_LABEL: Record<UserKind, string> = {
    [UserKind.Company]: 'Da empresa',
    [UserKind.Client]: 'De clientes',
};

export enum VehicleCalculationType {
    Doe = 1,
    Dou = 2,
    Newspaper = 3,
}

export const VEHICLE_CALCULATION_LABEL: Record<VehicleCalculationType, string> = {
    [VehicleCalculationType.Doe]: 'DOE',
    [VehicleCalculationType.Dou]: 'DOU',
    [VehicleCalculationType.Newspaper]: 'Jornal de grande circulação',
};

// Dias de circulação: 1 (domingo) a 7 (sábado)
export const WEEKDAY_LABEL: Record<number, string> = {
    1: 'Domingo', 2: 'Segunda', 3: 'Terça', 4: 'Quarta', 5: 'Quinta', 6: 'Sexta', 7: 'Sábado',
};

export const WEEKDAY_SHORT: Record<number, string> = {
    1: 'Dom', 2: 'Seg', 3: 'Ter', 4: 'Qua', 5: 'Qui', 6: 'Sex', 7: 'Sáb',
};

export enum BannerType {
    Integrated = 1,
    Popup = 2,
    Both = 3,
}

export const BANNER_TYPE_LABEL: Record<BannerType, string> = {
    [BannerType.Integrated]: 'Carrossel',
    [BannerType.Popup]: 'Popup',
    [BannerType.Both]: 'Carrossel e popup',
};

export enum AuditAction {
    Created = 1,
    Updated = 2,
    Deleted = 3,
    Activated = 4,
    Deactivated = 5,
    Linked = 6,
    Unlinked = 7,
    AccessSent = 8,
}

export const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
    [AuditAction.Created]: 'Criado',
    [AuditAction.Updated]: 'Alterado',
    [AuditAction.Deleted]: 'Excluído',
    [AuditAction.Activated]: 'Ativado',
    [AuditAction.Deactivated]: 'Desativado',
    [AuditAction.Linked]: 'Vinculado',
    [AuditAction.Unlinked]: 'Desvinculado',
    [AuditAction.AccessSent]: 'Acesso enviado',
};

export const AUDIT_ACTION_COLOR: Record<AuditAction, ChipColor> = {
    [AuditAction.Created]: 'success',
    [AuditAction.Updated]: 'info',
    [AuditAction.Deleted]: 'error',
    [AuditAction.Activated]: 'success',
    [AuditAction.Deactivated]: 'warning',
    [AuditAction.Linked]: 'primary',
    [AuditAction.Unlinked]: 'warning',
    [AuditAction.AccessSent]: 'secondary',
};

export enum AuditRecordType {
    Company = 1,
    EmailSettings = 2,
    NewsCategory = 3,
    News = 4,
    LinkCategory = 5,
    Link = 6,
    Banner = 7,
    Client = 8,
    ClientModule = 9,
    ClientVehicle = 10,
    DocumentType = 11,
    PublicationAgency = 12,
    PublicationVehicle = 13,
    AccessProfile = 14,
    AccessGroup = 15,
    User = 16,
    CompanyUser = 17,
    ClientUser = 18,
}

export const AUDIT_RECORD_TYPE_LABEL: Record<AuditRecordType, string> = {
    [AuditRecordType.Company]: 'Empresa',
    [AuditRecordType.EmailSettings]: 'Configuração de e-mail',
    [AuditRecordType.NewsCategory]: 'Categoria de notícia',
    [AuditRecordType.News]: 'Notícia',
    [AuditRecordType.LinkCategory]: 'Categoria de link',
    [AuditRecordType.Link]: 'Link',
    [AuditRecordType.Banner]: 'Banner',
    [AuditRecordType.Client]: 'Cliente',
    [AuditRecordType.ClientModule]: 'Módulo do cliente',
    [AuditRecordType.ClientVehicle]: 'Veículos do cliente',
    [AuditRecordType.DocumentType]: 'Tipo de documento',
    [AuditRecordType.PublicationAgency]: 'Órgão de publicação',
    [AuditRecordType.PublicationVehicle]: 'Veículo de publicação',
    [AuditRecordType.AccessProfile]: 'Perfil de acesso',
    [AuditRecordType.AccessGroup]: 'Grupo de acesso',
    [AuditRecordType.User]: 'Usuário',
    [AuditRecordType.CompanyUser]: 'Usuário × empresa',
    [AuditRecordType.ClientUser]: 'Usuário × cliente',
};

// Fila do Worker do GED (GET /GedProcessing/Item): os nomes são os do contrato; a prioridade usa os rótulos do GED
export type GedQueueStatus = 'pending' | 'processing' | 'done' | 'failed';

export const GED_QUEUE_STATUSES: GedQueueStatus[] = ['pending', 'processing', 'done', 'failed'];

export const GED_QUEUE_STATUS_LABEL: Record<GedQueueStatus, string> = {
    pending: 'Na fila',
    processing: 'Processando',
    done: 'Concluído',
    failed: 'Falha',
};

export const GED_QUEUE_STATUS_COLOR: Record<GedQueueStatus, ChipColor> = {
    pending: 'info',
    processing: 'warning',
    done: 'success',
    failed: 'error',
};

export const GED_QUEUE_TYPE_LABEL: Record<string, string> = {
    extraction: 'Extração de texto/OCR',
    reindex: 'Reindexação',
};

// Limites da API (validados de novo no servidor)
export const ADM_LIMITS = {
    justification: 2000,
    clientName: 120,
    personName: 120,
    email: 250,
    url: 250,
    login: { min: 3, max: 20 },
    phone: 20,
    registration: 20,
    zipCode: 8,
    street: 250,
    addressNumber: 15,
    complement: 80,
    neighborhood: 100,
    groupName: 150,
    profileName: 120,
    profileDescription: 1000,
    vehicleName: 120,
    agencyName: 120,
    documentType: 60,
    newsCategory: 60,
    linkCategory: 100,
    linkName: 150,
    newsTitle: 250,
    newsSource: 100,
    bannerTitle: 150,
    daysBefore: { min: 0, max: 30 },
    salePercentage: 999.99,
    // Logo enviada (depois do editor); o arquivo escolhido pode ser maior, já que é reduzido no navegador
    logoMaxSizeMb: 1,
    logoSourceMaxSizeMb: 20,
    // Maior lado da logo recortada, em px (sem ampliar)
    logoSide: 1024,
};

// Login: letras, números, ponto, hífen e sublinhado (gravado em minúsculas)
export const LOGIN_PATTERN = /^[A-Za-z0-9._-]+$/;

// Serviços em segundo plano das APIs (GET /BackgroundService): situação do serviço e de cada instância
export type BackgroundServiceStatus = 'ok' | 'running' | 'failing' | 'late' | 'stopped' | 'disabled' | 'unknown';

export const BACKGROUND_SERVICE_STATUS_LABEL: Record<BackgroundServiceStatus, string> = {
    ok: 'Em dia',
    running: 'Em execução',
    failing: 'Com falha',
    late: 'Atrasado',
    stopped: 'Parado',
    disabled: 'Desligado',
    unknown: 'Sem registro',
};

export const BACKGROUND_SERVICE_STATUS_COLOR: Record<BackgroundServiceStatus, ChipColor> = {
    ok: 'success',
    running: 'info',
    failing: 'error',
    late: 'warning',
    stopped: 'error',
    disabled: 'default',
    unknown: 'default',
};
